import { useState, useEffect, useRef } from 'react'
import { connect } from 'react-redux'
import { debounce } from 'lodash'
import * as PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'

import TextField from '../../FormLegacy/FormFieldsV2/TextField'
import InfiniteScroll from '../../InfiniteScroll/InfiniteScroll'
import { TwilioService } from '../services/twilio-service'
import { fetchShiftManagers } from '../../../reducers/Organisation/shiftManagersReducer'
import {
  selectClients,
  selectStatusOfLoading,
  setLoadingState,
  selectStatusIsLoadedAll,
} from '../../../reducers/clientsReducer'
import { dispatchSetConversations, selectConversations } from '../../../reducers/chatReducer'
import API from '../../../api/api'

import ContactItem from './ContactItem'

const ChatContactsWindow = ({
  selectedInbox,
  chatContacts,
  handleCancel,
  contacts,
  fetchContacts,
  isLoading,
  setLoadingState,
  isLoadedAll,
  conversations,
  dispatchSetConversations,
  openChat,
}) => {
  const { t } = useTranslation(['chat'])
  const [isSubmiting, setSubmiting] = useState(false)
  const [search, setSearch] = useState('')
  const [prevSearch, setPrevSearch] = useState('')
  const timeout = useRef(null)
  const debounceFn = debounce(fetchContacts, 1000)

  const onCreateOrJoin = async (responderId) => {
    setSubmiting(true)

    const inboxType = selectedInbox?.type ?? 'ALL'

    const dataAboutChat = {
      teamType: inboxType,
      conversationUserId: responderId,
    }

    if (inboxType === 'CUST') {
      dataAboutChat.customInboxId = selectedInbox.id
    }

    API.createOrJoinConversation(dataAboutChat)
      .then(async (conversation) => {
        const newConversation = TwilioService.getInstance().parseConversation(conversation)
        const person =
          chatContacts &&
          chatContacts.find(
            (contact) =>
              Number(contact.id) ===
              Number(newConversation.name.slice(0, newConversation.name.indexOf('_'))),
          )
        const conversationToCreate = {
          ...newConversation,
          channelName: newConversation.name,
          avatar: person ? person.avatar : '',
          name: person ? `${person.firstName} ${person.lastName}` : '',
          role: person ? person.role : '',
          email: person ? person.email : '',
          phone: person ? person.phone : '',
          apartment: person ? person.apartment : '',
          address: person ? `${person.street} #${person.apartment}` : '',
        }

        await dispatchSetConversations(conversations.concat(conversationToCreate))
        openChat(conversationToCreate.channelName)
        timeout.current = setTimeout(() => {
          setSubmiting(false)
          handleCancel()
        }, 1000)
      })
      .catch((err) => {
        console.error(err.message)
        setSubmiting(false)
      })
  }

  useEffect(() => {
    if (prevSearch !== search) {
      window.addEventListener('keydown', debounceFn(search))
    }
    setPrevSearch(search)
    return () => {
      window.removeEventListener('keydown', debounceFn)
    }
  }, [search])

  useEffect(() => {
    return () => {
      if (timeout.current) clearTimeout(timeout.current)
    }
  }, [])

  return (
    <div className="chat-contacts-window">
      <div className="chat-contacts-window__container">
        <h1>{t('chat:chatContactsWindow.title')}</h1>

        <TextField
          maxLength="50"
          name={'title'}
          placeholder={t('chat:chatContactsWindow.searchPlaceholder')}
          onChange={(e) => {
            setSearch(e.target.value)
            setLoadingState(true)
          }}
          value={search}
        />

        <div className={`contacts-list ${isSubmiting ? 'is-loading' : ''}`}>
          {isLoading ? (
            <div>{t('chat:chatContactsWindow.loading')}</div>
          ) : contacts.length ? (
            <InfiniteScroll
              data={contacts}
              renderItem={(item) => (
                <ContactItem data={item} onClick={() => onCreateOrJoin(item.user.id)} />
              )}
              onEndReached={() => fetchContacts(search, true)}
              style={{ height: '100%', paddingRight: '12px' }}
              isLoadedAll={isLoadedAll}
            />
          ) : (
            <div>{t('chat:searchAndSelectWindow.noResults')}</div>
          )}
        </div>

        <div className="columns">
          <div className="column is-8">
            <button
              type="submit"
              className={`button button-gray full-width ${isSubmiting ? 'is-loading' : ''}`}
              onClick={() => handleCancel()}
            >
              {t('common:actions.close')}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

ChatContactsWindow.propTypes = {
  selectedInbox: PropTypes.string,
  chatContacts: PropTypes.array,
  handleCancel: PropTypes.func,
  contacts: PropTypes.array,
  fetchContacts: PropTypes.func,
  isLoading: PropTypes.bool,
  setLoadingState: PropTypes.func,
  isLoadedAll: PropTypes.bool,
  conversations: PropTypes.array,
  dispatchSetConversations: PropTypes.func,
  openChat: PropTypes.func,
  fetchShiftManagers: PropTypes.func,
}

export default connect(
  (state) => {
    return {
      contacts: selectClients(state),
      isLoading: selectStatusOfLoading(state),
      isLoadedAll: selectStatusIsLoadedAll(state),
      conversations: selectConversations(state),
    }
  },
  {
    setLoadingState,
    dispatchSetConversations,
    fetchShiftManagers,
  },
)(ChatContactsWindow)
